import { AnimatePresence, m } from 'motion/react';
import { Check, Info, TriangleAlert, X } from 'lucide-react';
import { useEffect } from 'react';
import { create } from 'zustand';

import { cn } from '../lib/cn';

/**
 * El aviso efímero que el producto no tenía.
 *
 * Hasta ahora, guardar un formulario no decía nada: la pantalla se quedaba
 * igual y había que deducir del silencio que había funcionado. `13 §6` describe
 * los errores con detalle pero no el acuse de recibo, y sin él las pantallas de
 * ajustes se sienten rotas aunque funcionen.
 *
 * Vive en un store y no en un contexto porque quien más lo necesita son los
 * `onSuccess` de las mutaciones, que están fuera del árbol de React.
 */
export type ToastTone = 'success' | 'error' | 'info';

interface Toast {
  id: number;
  tone: ToastTone;
  message: string;
}

interface ToastState {
  toasts: Toast[];
  push: (tone: ToastTone, message: string) => void;
  dismiss: (id: number) => void;
}

let seq = 0;

const useToasts = create<ToastState>((set) => ({
  toasts: [],
  push: (tone, message) =>
    set((state) => ({
      // Cuatro a la vez es el límite: una pila más alta tapa la pantalla justo
      // cuando el usuario quiere ver el resultado de lo que ha hecho.
      toasts: [...state.toasts, { id: (seq += 1), tone, message }].slice(-4),
    })),
  dismiss: (id) => set((state) => ({ toasts: state.toasts.filter((toast) => toast.id !== id) })),
}));

export const toast = {
  success: (message: string) => useToasts.getState().push('success', message),
  error: (message: string) => useToasts.getState().push('error', message),
  info: (message: string) => useToasts.getState().push('info', message),
};

const TONES: Record<ToastTone, { icon: typeof Check; className: string }> = {
  success: { icon: Check, className: 'border-success-line bg-success-pale text-success' },
  error: { icon: TriangleAlert, className: 'border-danger-line bg-danger-pale text-danger' },
  info: { icon: Info, className: 'border-info-line bg-info-pale text-info' },
};

function ToastRow({ toast: item }: { toast: Toast }) {
  const dismiss = useToasts((state) => state.dismiss);
  const { icon: Icon, className } = TONES[item.tone];

  // Los errores no se van solos: son los que el usuario necesita releer.
  useEffect(() => {
    if (item.tone === 'error') {
      return;
    }

    const timer = setTimeout(() => dismiss(item.id), 4200);

    return () => clearTimeout(timer);
  }, [item.id, item.tone, dismiss]);

  return (
    <m.li
      layout
      initial={{ opacity: 0, x: 28, scale: 0.96 }}
      animate={{ opacity: 1, x: 0, scale: 1 }}
      exit={{ opacity: 0, x: 28, scale: 0.96, transition: { duration: 0.16 } }}
      transition={{ type: 'spring', stiffness: 420, damping: 34 }}
      className={cn(
        'pointer-events-auto flex items-start gap-3 rounded-md border px-4 py-3 text-sm shadow-lg backdrop-blur',
        className,
      )}
    >
      <Icon className="mt-px size-4 shrink-0" aria-hidden="true" />
      <span className="grow text-ink">{item.message}</span>
      <button
        type="button"
        onClick={() => dismiss(item.id)}
        className="-m-1 shrink-0 rounded p-1 text-ink-faint transition-colors hover:text-ink"
      >
        <X className="size-3.5" aria-hidden="true" />
      </button>
    </m.li>
  );
}

export function ToastHost() {
  const toasts = useToasts((state) => state.toasts);

  return (
    <ul
      // `aria-live` para que un lector de pantalla anuncie el acuse de recibo:
      // sin esto el aviso es información que solo existe para quien mira.
      aria-live="polite"
      className="pointer-events-none fixed inset-x-4 bottom-4 z-100 flex flex-col items-end gap-2 sm:left-auto sm:right-6 sm:w-96"
    >
      <AnimatePresence initial={false}>
        {toasts.map((item) => (
          <ToastRow key={item.id} toast={item} />
        ))}
      </AnimatePresence>
    </ul>
  );
}
